<script>
  import { onDestroy } from 'svelte';
  import { ArrowUpRight, Calendar, LoaderCircle, Search, User, X } from 'lucide-svelte';
  import { get } from 'svelte/store';
  import {
    myId,
    resetSearch,
    searchCursor,
    searchError,
    searchHasMore,
    searchLoading,
    searchMessagesWS,
    searchResults
  } from '$lib/websocket-client';

  export let conversationId = null;
  export let onClose = () => {};
  export let closeOnJump = false;

  let keyword = '';
  let senderFilter = 'all';
  let dateFilter = '';
  let debounceTimer;
  let lastQueryKey = '';

  function escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function highlight(text = '', search = '') {
    const safeText = escapeHtml(text);
    const normalized = search.trim();
    if (!normalized) return safeText;
    const escaped = normalized.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return safeText.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>');
  }

  function formatDate(value) {
    if (!value) return '';
    return new Date(value).toLocaleString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  function runSearch(before = null) {
    if (!conversationId) return;
    searchMessagesWS({ conversationId, keyword, senderFilter, dateFilter, before, limit: 20 });
  }

  $: {
    const hasFilter = Boolean(keyword.trim() || dateFilter || senderFilter !== 'all');
    const queryKey = `${conversationId}|${keyword.trim()}|${senderFilter}|${dateFilter}`;

    if (!conversationId || !hasFilter) {
      clearTimeout(debounceTimer);
      if (lastQueryKey) resetSearch();
      lastQueryKey = '';
    } else if (queryKey !== lastQueryKey) {
      lastQueryKey = queryKey;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => runSearch(), 280);
    }
  }

  function loadMore() {
    const cursor = get(searchCursor);
    if (!cursor || get(searchLoading) || !get(searchHasMore)) return;
    runSearch(cursor);
  }

  function clearFilters() {
    keyword = '';
    senderFilter = 'all';
    dateFilter = '';
    lastQueryKey = '';
    resetSearch();
  }

  function jumpToOriginal(message) {
    if (!message?.id) return;
    window.dispatchEvent(new CustomEvent('search-jump-to-message', {
      detail: { messageId: message.id, conversationId, keyword }
    }));
    if (closeOnJump) onClose?.();
  }

  onDestroy(() => {
    clearTimeout(debounceTimer);
    resetSearch();
  });
</script>

<section class="message-search">
  <header>
    <div>
      <span class="eyebrow">Tìm trong trò chuyện</span>
      <h2>Lịch sử tin nhắn</h2>
    </div>
    <button class="icon-button" type="button" aria-label="Đóng tìm kiếm" onclick={onClose}><X size={18} /></button>
  </header>

  <div class="search-tools">
    <label class="search-input">
      <Search size={17} />
      <input bind:value={keyword} autocomplete="off" placeholder="Nhập nội dung cần tìm..." />
      {#if keyword}<button type="button" onclick={() => (keyword = '')}><X size={14} /></button>{/if}
    </label>

    <div class="filters">
      <label>
        <User size={15} />
        <select bind:value={senderFilter}>
          <option value="all">Mọi người</option>
          <option value="me">Tin nhắn của tôi</option>
          <option value="other">Người khác</option>
        </select>
      </label>
      <label>
        <Calendar size={15} />
        <input type="date" bind:value={dateFilter} />
      </label>
    </div>
  </div>

  <div class="result-heading">
    <div>
      <strong>Kết quả</strong>
      <span>{$searchResults.length} tin nhắn</span>
    </div>
    {#if keyword || dateFilter || senderFilter !== 'all'}
      <button type="button" onclick={clearFilters}>Xóa bộ lọc</button>
    {/if}
  </div>

  <main class="results">
    {#if $searchLoading && !$searchResults.length}
      <div class="state"><LoaderCircle size={19} class="spin" /><span>Đang tìm kiếm...</span></div>
    {:else if $searchError}
      <div class="state error"><strong>Không thể tìm kiếm</strong><span>{$searchError}</span></div>
    {:else if !keyword.trim() && !dateFilter && senderFilter === 'all'}
      <div class="state"><Search size={22} /><strong>Tìm lại nội dung quan trọng</strong><span>Nhập từ khóa hoặc chọn bộ lọc để bắt đầu.</span></div>
    {:else if !$searchResults.length}
      <div class="state"><strong>Không có kết quả phù hợp</strong><span>Thử từ khóa ngắn hơn hoặc thay đổi bộ lọc.</span></div>
    {:else}
      <div class="result-list">
        {#each $searchResults as message (message.id)}
          <article>
            <div class="result-meta">
              <strong>{message.from === $myId ? 'Bạn' : message.fromName || 'Người gửi'}</strong>
              <time>{formatDate(message.created_at)}</time>
            </div>
            <p>{@html highlight(message.message || '', keyword)}</p>
            <a
              href={`#msg-${message.id}`}
              onclick={(event) => {
                event.preventDefault();
                jumpToOriginal(message);
              }}>
              <span>Xem tin nhắn gốc</span><ArrowUpRight size={15} />
            </a>
          </article>
        {/each}
      </div>

      {#if $searchHasMore}
        <button class="load-more" type="button" disabled={$searchLoading} onclick={loadMore}>
          {#if $searchLoading}<LoaderCircle size={15} class="spin" />{/if}
          <span>{$searchLoading ? 'Đang tải...' : 'Xem thêm kết quả'}</span>
        </button>
      {/if}
    {/if}
  </main>
</section>

<style>
  .message-search { display:flex; flex-direction:column; width:100%; height:100%; min-height:0; overflow:hidden; color:#0f172a; background:linear-gradient(180deg,#fff,#f8fafc); }
  header,.result-heading { display:flex; align-items:center; justify-content:space-between; gap:.75rem; padding:.9rem 1rem; border-bottom:1px solid rgba(226,232,240,.85); background:rgba(255,255,255,.84); backdrop-filter:blur(18px); }
  .eyebrow { color:#0284c7; font-size:.62rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; }
  h2 { margin-top:.18rem; font-size:.98rem; font-weight:750; }
  .icon-button,.search-input button { display:grid; place-items:center; width:2rem; height:2rem; border-radius:999px; color:#475569; background:rgba(226,232,240,.72); }
  .search-tools { display:grid; gap:.7rem; padding:.85rem 1rem; border-bottom:1px solid rgba(226,232,240,.75); }
  .search-input,.filters label { display:flex; align-items:center; gap:.55rem; border:1px solid rgba(148,163,184,.22); color:#64748b; background:#fff; }
  .search-input { height:2.75rem; padding:0 .75rem; border-radius:1rem; box-shadow:0 10px 24px rgba(15,23,42,.04); }
  .search-input:focus-within { border-color:rgba(14,165,233,.5); box-shadow:0 0 0 3px rgba(14,165,233,.1); }
  input,select { min-width:0; width:100%; color:#0f172a; background:transparent; outline:none; font-size:.75rem; }
  .filters { display:grid; grid-template-columns:1fr 1fr; gap:.55rem; }
  .filters label { height:2.35rem; padding:0 .65rem; border-radius:.8rem; }
  .result-heading strong { display:block; font-size:.78rem; }
  .result-heading span { color:#94a3b8; font-size:.66rem; }
  .result-heading button { color:#0284c7; font-size:.68rem; font-weight:700; }
  .results { flex:1; min-height:0; overflow:auto; padding:.8rem; overscroll-behavior:contain; scrollbar-gutter:stable; }
  .state { display:grid; place-items:center; gap:.4rem; min-height:12rem; padding:1rem; color:#64748b; text-align:center; font-size:.75rem; }
  .state strong { color:#334155; font-size:.8rem; }
  .state.error strong { color:#be123c; }
  .result-list { display:grid; gap:.65rem; }
  article { padding:.8rem; border:1px solid rgba(226,232,240,.9); border-radius:1rem; background:rgba(255,255,255,.9); box-shadow:0 8px 22px rgba(15,23,42,.04); }
  .result-meta { display:flex; align-items:center; justify-content:space-between; gap:.5rem; }
  .result-meta strong { color:#0369a1; font-size:.7rem; }
  time { color:#94a3b8; font-size:.62rem; }
  article p { margin-top:.45rem; color:#334155; font-size:.75rem; line-height:1.55; overflow-wrap:anywhere; }
  article p :global(mark) { padding:0 .12rem; border-radius:.2rem; color:#713f12; background:#fef08a; }
  article a { display:inline-flex; align-items:center; gap:.25rem; margin-top:.55rem; color:#0284c7; font-size:.68rem; font-weight:750; }
  .load-more { display:flex; align-items:center; justify-content:center; gap:.4rem; width:100%; margin-top:.75rem; padding:.65rem; border:1px solid rgba(148,163,184,.22); border-radius:.85rem; color:#475569; background:white; font-size:.72rem; font-weight:700; }
  :global(.spin) { animation:spin 1s linear infinite; }
  @keyframes spin { to { transform:rotate(360deg); } }
  @media(max-width:640px){ .filters{grid-template-columns:1fr}.results{padding:.7rem} }
</style>
